if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      product = null;
      variantsMap = new Map();
      preProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        const productJsonElement = this.getJsonScriptElement();
        if (productJsonElement) {
          try {
            this.product = JSON.parse(productJsonElement.textContent);
            this.product.variants.forEach((variant) => {
              this.variantsMap.set(variant.id, variant);
            });
          } catch (e) {
            console.error('Failed to parse product JSON', e);
            this.product = null;
          }
        }

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
        this.initQuantityHandlers();
        this.setInitialVariantState();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }
      
      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      getJsonScriptElement() {
         // In the quick-add modal, the script is a direct child of the modal content, which is the parent of this element.
        const quickAddParent = this.parentElement;
        if(quickAddParent){
            const script = quickAddParent.querySelector(`script[id^="product-json-"]`);
            if(script) return script;
        }
        // Fallback for product page
        return document.getElementById(`product-json-${this.sectionId}`);
      }

      setInitialVariantState() {
        if (!this.product) return;
        const currentVariantId = this.querySelector('input[name="id"]')?.value;
        const initialVariant = this.variantsMap.get(parseInt(currentVariantId, 10));

        if (initialVariant) {
          this.renderProductInfo(initialVariant, true);
        } else {
          this.setUnavailable();
        }
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;
        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target) || !this.product) return;

        const selectedVariant = this.product.variants.find((variant) =>
          variant.options.every((optionValue, index) => selectedOptionValues[index] === optionValue)
        );

        this.resetProductFormState();

        if (selectedVariant) {
          this.renderProductInfo(selectedVariant);
        } else {
          this.setUnavailable();
        }
      }

      renderProductInfo(variant, isInitialLoad = false) {
        this.updateURL(this.dataset.url, variant.id);
        this.updateVariantInputs(variant.id);
        this.updatePrice(variant);
        this.updateSku(variant);
        this.updateInventory(variant);
        if(!isInitialLoad){
          this.updateMedia(variant.featured_media);
        }
        this.updateQuantityRules(variant);

        const addButtonText = variant.available ? window.variantStrings.addToCart : window.variantStrings.soldOut;
        this.productForm?.toggleSubmitButton(!variant.available, addButtonText);

        this.pickupAvailability?.update(variant);

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            variant: variant,
          },
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.handleErrorMessage();
      }

      updatePrice(variant) {
        const priceElement = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceElement) return;
        
        const salePriceEl = priceElement.querySelector('.price-item--sale .price__container');
        const regularPriceEl = priceElement.querySelector('.price-item--regular .price__container s');

        if (Shopify && Shopify.formatMoney) {
          if (variant.compare_at_price > variant.price) {
             if (salePriceEl) salePriceEl.innerHTML = Shopify.formatMoney(variant.price, window.moneyFormat);
             if (regularPriceEl) regularPriceEl.innerHTML = Shopify.formatMoney(variant.compare_at_price, window.moneyFormat);
             priceElement.classList.add('price--on-sale');
          } else {
             if (salePriceEl) salePriceEl.innerHTML = Shopify.formatMoney(variant.price, window.moneyFormat);
             priceElement.classList.remove('price--on-sale');
          }
        }
      }

      updateSku(variant) {
        const skuElement = this.querySelector(`#Sku-${this.dataset.section}`);
        if (skuElement) {
          skuElement.textContent = variant.sku || '';
          skuElement.classList.toggle('visibility-hidden', !variant.sku);
        }
      }
      
      updateInventory(variant) {
        const inventoryElement = this.querySelector(`#Inventory-${this.dataset.section}`);
        if (inventoryElement) {
            inventoryElement.classList.toggle('hidden', variant.inventory_management !== 'shopify');
            if (variant.inventory_management === 'shopify') {
                let newHtml = '';
                const lowStockThreshold = 10; 
                if (variant.inventory_quantity > 0) {
                    const iconColor = variant.inventory_quantity <= lowStockThreshold ? 'rgb(238, 148, 65)' : 'rgb(62, 214, 96)';
                    const icon = `<span class="svg-wrapper" style="color: ${iconColor}">${this.getIcon('icon-inventory-status')}</span>`;
                    const text = (variant.inventory_quantity <= lowStockThreshold ? window.variantStrings.lowStock : window.variantStrings.inStock) || '';
                    newHtml = `${icon} ${text.replace('[quantity]', variant.inventory_quantity)}`;
                } else {
                    if (variant.inventory_policy === 'continue') {
                        newHtml = `<span class="svg-wrapper" style="color: rgb(62, 214, 96)">${this.getIcon('icon-inventory-status')}</span> ${window.variantStrings.continueSelling}`;
                    } else {
                        newHtml = `<span class="svg-wrapper" style="color: rgb(200, 200, 200)">${this.getIcon('icon-inventory-status')}</span> ${window.variantStrings.soldOut}`;
                    }
                }
                inventoryElement.innerHTML = newHtml;
            }
        }
    }
    
    getIcon(name) {
      // This is a placeholder. A robust solution would use a proper icon system.
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none" stroke="currentColor"><circle cx="6" cy="6" r="5.5"/></svg>';
    }

      updateVariantInputs(variantId) {
        this.querySelectorAll(`[name="id"]`).forEach(
          (input) => {
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        );
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`);
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        const selectors = ['price', 'Inventory', 'Sku']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        this.querySelectorAll(selectors).forEach((element) => element.classList.add('hidden'));
      }

      updateMedia(featuredMedia) {
        if (!featuredMedia) return;
        const mediaGallery = this.querySelector('media-gallery');
        mediaGallery?.setActiveMedia(`${this.dataset.section}-${featuredMedia.id}`, true);
      }

      setQuantityBoundries() {
         if (!this.quantityInput) return;
         // This logic remains largely the same
      }
      
      updateQuantityRules(variant) {
        if (!this.quantityInput || !variant) return;

        this.quantityInput.setAttribute('data-min', variant.quantity_rule.min);
        this.quantityInput.setAttribute('data-max', variant.quantity_rule.max || '');
        this.quantityInput.setAttribute('step', variant.quantity_rule.increment);

        this.setQuantityBoundries();
      }

      fetchQuantityRules() {
        const variantId = this.querySelector('input[name="id"]')?.value;
        const variant = this.variantsMap.get(parseInt(variantId, 10));
        if (variant) {
          this.updateQuantityRules(variant);
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
